import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faExpand,
  faCompress,
} from '@fortawesome/free-solid-svg-icons';
import { useIframeStore } from '../../store/useIframe';
import { useState, useEffect } from 'react';

/**
 * @author
 * @date 2025.10.28
 * @description Iframe 패널 컴포넌트
 */
const IframePanel = () => {
  const { isOpen, url, closeIframe, isFullscreen, setIsFullscreen, currentTutorialStep } = useIframeStore();
  const [showButtonHighlight, setShowButtonHighlight] = useState(false);
  
  useEffect(() => {
    // 전체화면 튜토리얼 진행 중인지 확인 (Step 0.9)
    const pathname = window.location.pathname;
    if (pathname === '/bizmsg/sender' && currentTutorialStep === 0.9 && !isFullscreen) {
      setShowButtonHighlight(true);
    } else {
      setShowButtonHighlight(false);
    }
  }, [currentTutorialStep, isFullscreen]);

  if (!isOpen) return null;

  return (
    <div
      className={`!bg-white !border-l !border-gray-300 ${
        isFullscreen ? '!fixed !inset-0 !w-screen !h-screen !z-[99999999]' : '!relative !h-full'
      }`}
    >
      {/* 플로팅 컨트롤 버튼 - 우측 상단 */}
      <div className="!absolute !top-10 !right-10 !z-[999999999]">
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="!w-10 !h-10 !bg-white !text-gray-600 hover:!text-gray-800 hover:!bg-gray-100 !rounded-full !shadow-lg hover:!shadow-xl !border !border-gray-200 !transition-all !duration-200 !flex !items-center !justify-center"
          title={isFullscreen ? '전체화면 종료' : '전체화면'}
        >
          <FontAwesomeIcon
            icon={isFullscreen ? faCompress : faExpand}
            className="!text-sm"
          />
        </button>
      </div>
      
      {/* 버튼 강조 애니메이션 */}
      {showButtonHighlight && (
        <>
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes arrowPoint {
                0%, 100% {
                  transform: translateX(0);
                }
                50% {
                  transform: translateX(-5px);
                }
              }
            `
          }} />
          
          {/* 화살표 및 텍스트 강조 - 좌측 배치 */}
          <div 
            style={{
              position: 'absolute',
              top: '35px',
              right: '90px',
              zIndex: 999999998,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div
              style={{
                backgroundColor: '#ff4757',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '18px',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(255, 71, 87, 0.4)',
                whiteSpace: 'nowrap'
              }}
            >
              우측 전체화면 버튼을 클릭해주세요
            </div>
            <svg
              width="40"
              height="30"
              viewBox="0 0 40 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ animation: 'arrowPoint 1.5s ease-in-out infinite' }}
            >
              <path
                d="M5 15 L25 15 M18 7 L26 15 L18 23"
                stroke="#ff4757"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </>
      )}

      {/* iframe 컨텐츠 - 전체 영역 */}
      <div className="!w-full !h-full !relative !overflow-hidden">
        {url ? (
          <iframe
            src={url}
            className="!w-full !h-full !border-none"
            title="Reference Page"
            style={{ zoom: 0.8 }}
          />
        ) : (
          <div className="!flex !items-center !justify-center !h-full !text-gray-500">
            <p>참고할 URL이 설정되지 않았습니다.</p>
          </div>
        )}
      </div>
      
      {/* 전체화면 상태에서 안내 메시지 */}
      {isFullscreen && currentTutorialStep === 0.9 && (
        <>
          <div
            style={{
              position: 'fixed',
              bottom: '60px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999999999,
              backgroundColor: '#2d3436',
              color: 'white',
              padding: '30px 50px',
              borderRadius: '60px',
              fontSize: '28px',
              fontWeight: '800',
              boxShadow: '0 15px 40px rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              animation: 'slideUp 0.5s ease-out'
            }}
          >
            <FontAwesomeIcon icon={faCompress} style={{ fontSize: '32px' }} />
            <span>이제 전체화면 종료 버튼을 눌러 돌아가세요!</span>
          </div>
          
          {/* 전체화면 종료 버튼 강조 */}
          <div
            style={{
              position: 'fixed',
              top: '35px',
              right: '90px',
              zIndex: 9999999998,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'fadeIn 0.5s ease-out 0.5s both'
            }}
          >
            <div
              style={{
                backgroundColor: '#FFC107',
                color: '#212529',
                padding: '16px 28px',
                borderRadius: '30px',
                fontSize: '20px',
                fontWeight: '800',
                boxShadow: '0 8px 20px rgba(255, 193, 7, 0.5), 0 0 0 3px rgba(255, 193, 7, 0.3)',
                whiteSpace: 'nowrap',
                border: '3px solid white',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            >
              🔥 우측 전체화면 종료 버튼을 클릭하세요!
            </div>
            <svg
              width="50"
              height="40"
              viewBox="0 0 50 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ animation: 'arrowPoint 1.5s ease-in-out infinite' }}
            >
              <path
                d="M5 20 L35 20 M25 10 L35 20 L25 30"
                stroke="#FFC107"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
              />
            </svg>
          </div>
        </>
      )}
      
      {/* 애니메이션 추가 */}
      {isFullscreen && currentTutorialStep === 0.9 && (
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes slideUp {
              from {
                transform: translate(-50%, 100px);
                opacity: 0;
              }
              to {
                transform: translate(-50%, 0);
                opacity: 1;
              }
            }
            
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateX(20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                box-shadow: 0 8px 20px rgba(255, 193, 7, 0.5), 0 0 0 3px rgba(255, 193, 7, 0.3);
              }
              50% {
                transform: scale(1.05);
                box-shadow: 0 12px 30px rgba(255, 193, 7, 0.7), 0 0 0 5px rgba(255, 193, 7, 0.4);
              }
            }
          `
        }} />
      )}
    </div>
  );
};

export default IframePanel;
